import React from 'react';
import {
  PossibleImageSizes,
  CountryCodes,
  CandidateOpportunitiesConst,
  DisplayPronouns,
} from '@axiom/const';
import type {
  AfcSubmission,
  AfcSubmissionCandidateOpportunity,
} from '@axiom/validation';
import { CandidateYearsOfExperienceModel } from '@axiom/ui-models';
import {
  Button,
  CandidateBadges,
  CandidateProfileUtil,
  CondensedMedium,
  ContextMenu,
  ContextMenuItem,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  ImageCircle,
  Layout,
  LayoutItem,
  SectionHeader,
  useBreakpoint,
} from '@axiom/ui';

export type CandidateCardHeaderTypes = {
  candidateOpp: AfcSubmissionCandidateOpportunity;
  hideTalentImage?: boolean;
  submission?: AfcSubmission | null;
  showContextMenu?: boolean;
  onEngage: () => void;
  onPass: () => void;
  onInterview?: () => void;
  updatePinnedStatus?: () => Promise<void>;
};

export const CandidateCardHeader = ({
  candidateOpp,
  hideTalentImage = false,
  submission,
  showContextMenu = false,
  onEngage,
  onPass,
  onInterview,
  updatePinnedStatus,
}: CandidateCardHeaderTypes) => {
  const { isSmallScreen, isMobile } = useBreakpoint();
  const { candidate } = candidateOpp;
  const formatLocationForBios = () => {
    return candidate.addressCountry === CountryCodes.US
      ? [candidate.addressState, candidate.addressCountry]
          .filter(Boolean)
          .join(', ')
      : candidate.addressCountry;
  };

  const showCtaButtons = () => {
    return (
      submission?.isQualificationComplete &&
      candidateOpp.candidateStatus !==
        CandidateOpportunitiesConst.CandidateStatuses.Engaged &&
      candidateOpp.candidateStatus !==
        CandidateOpportunitiesConst.CandidateStatuses.Selected
    );
  };

  const showInterviewOrEngage = () => {
    if (
      !!onInterview &&
      candidateOpp.candidateStatus !==
        CandidateOpportunitiesConst.CandidateStatuses.Interviewing
    ) {
      return (
        <Gutter right="16px">
          <Button
            onClick={() => {
              onInterview();
            }}
            name="PENDO_REQUEST_INTERVIEW_CTA"
          >
            Interview
          </Button>
          <Gutter bottom="16px" only="mobile" />
        </Gutter>
      );
    }
    if (
      candidateOpp.candidateStatus ===
        CandidateOpportunitiesConst.CandidateStatuses.Interviewing &&
      candidateOpp.proposedHourlyRate
    ) {
      return (
        <Gutter right="16px">
          <Button
            onClick={() => {
              onEngage();
            }}
            name="ENGAGE_BUTTON"
          >
            Engage
          </Button>
          <Gutter bottom="16px" only="mobile" />
        </Gutter>
      );
    }

    return null;
  };

  return (
    <Grid columns={12} verticalAlign="middle" name="CANDIDATE_HEADER">
      <GridRow>
        <GridColumn largeScreenWidth={1} smallScreenWidth={12}>
          {!hideTalentImage && (
            <>
              <ImageCircle
                name="CANDIDATE_IMAGE"
                imageName={candidate.calculatedDisplayName}
                src={CandidateProfileUtil.getProfileImageUri(
                  candidate,
                  PossibleImageSizes.W_100
                )}
              />
              <Gutter bottom="16px" only="smallScreen" />
            </>
          )}
        </GridColumn>
        <GridColumn largeScreenWidth={5} smallScreenWidth={12}>
          <Layout position="right" direction="vertical">
            <Gutter left={isSmallScreen || !showContextMenu ? '0px' : '24px'}>
              <SectionHeader name="display-name" maxLines={2}>
                {candidate.calculatedDisplayName}
              </SectionHeader>

              {candidate.pronouns ? (
                <CondensedMedium name="CANDIDATE_PRONOUNS">
                  {DisplayPronouns[candidate.pronouns]}
                </CondensedMedium>
              ) : null}

              {!!hideTalentImage && (
                <CondensedMedium name="bio_location">
                  <Layout position="middle">{formatLocationForBios()}</Layout>
                </CondensedMedium>
              )}
              {Number.isInteger(candidate.yearsOfExperience) &&
                (candidate.yearsOfExperience ?? 0) >= 0 &&
                !hideTalentImage && (
                  <CondensedMedium name="candidate-header">
                    {`${new CandidateYearsOfExperienceModel(
                      candidate.yearsOfExperience
                    ).getYearsOfExperienceRange()} years experience`}
                  </CondensedMedium>
                )}
              <Gutter bottom="8px" />
              <CandidateBadges badges={candidate.badges} />
              <Gutter bottom="16px" only="smallScreen" />
            </Gutter>
          </Layout>
        </GridColumn>

        <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
          {showCtaButtons() && showContextMenu ? (
            <Layout position={isSmallScreen ? 'left' : 'right middle'} wrap>
              {!!updatePinnedStatus && !isMobile && (
                <Gutter right="16px">
                  <Button
                    variation="outline"
                    icon={candidateOpp.pinnedByClient ? 'pin-filled' : 'pin'}
                    name="PIN_BUTTON"
                    onClick={async () => updatePinnedStatus()}
                  >
                    {candidateOpp.pinnedByClient ? 'Pinned' : 'Pin'}
                  </Button>
                  <Gutter bottom="16px" only="mobile" />
                </Gutter>
              )}
              {showInterviewOrEngage()}
              <ContextMenu name="CANDIDATE_CARD_CONTEXT_MENU" direction="left">
                {!!updatePinnedStatus && isMobile ? (
                  <ContextMenuItem
                    name="PIN_CONTEXT_MENU_BUTTON"
                    onClick={async () => updatePinnedStatus()}
                  >
                    {candidateOpp.pinnedByClient ? 'Pinned' : 'Pin'}
                  </ContextMenuItem>
                ) : null}
                {!!candidateOpp.proposedHourlyRate &&
                  candidateOpp.candidateStatus !==
                    CandidateOpportunitiesConst.CandidateStatuses
                      .Interviewing && (
                    <ContextMenuItem
                      name="ENGAGE_CONTEXT_MENU_BUTTON"
                      onClick={() => {
                        onEngage();
                      }}
                    >
                      Engage
                    </ContextMenuItem>
                  )}
                <ContextMenuItem
                  name="PASS_CONTEXT_MENU_BUTTON"
                  onClick={() => {
                    onPass();
                  }}
                >
                  Pass
                </ContextMenuItem>
              </ContextMenu>
            </Layout>
          ) : (
            <Layout
              position={isSmallScreen ? 'left' : 'right middle'}
              wrap
              horizontalGutter="16px"
            >
              {showCtaButtons() && !showContextMenu && (
                <LayoutItem fluid={isMobile} position="center">
                  <Button
                    variation="outline"
                    fluid={isMobile}
                    onClick={() => {
                      onPass();
                    }}
                    name="PASS_BUTTON"
                  >
                    Pass
                  </Button>
                  <Gutter bottom="16px" only="mobile" />
                </LayoutItem>
              )}
              {!!candidateOpp.proposedHourlyRate &&
                !showContextMenu &&
                showCtaButtons() && (
                  <LayoutItem fluid={isMobile} position="center">
                    <Button
                      fluid={isMobile}
                      onClick={() => {
                        onEngage();
                      }}
                      name="ENGAGE_BUTTON"
                    >
                      Engage
                    </Button>
                  </LayoutItem>
                )}
            </Layout>
          )}
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
